<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useEventListener } from '@vueuse/core';

import { useDropdownPosition } from 'dashboard/composables/useDropdownPosition';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  labelMenuItems: {
    type: Array,
    default: () => [],
  },
  // Lets each surface say what the labels get attached to.
  pickerTitle: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['updateLabel']);

const { t } = useI18n();

const dropdownTitle = computed(
  () => props.pickerTitle || t('LABEL.PICKER_TITLE')
);

const showDropdown = ref(false);
const triggerRef = ref(null);
const dropdownRef = ref(null);

// The panels that render this button clip their overflow, so the menu is
// teleported to the body and positioned against the trigger instead.
const { fixedPosition, updatePosition } = useDropdownPosition(
  triggerRef,
  dropdownRef,
  showDropdown
);

// The menu is teleported out of the button's subtree, so closing it would drop
// focus on the body and lose the keyboard's place on the page.
const closeDropdown = () => {
  if (!showDropdown.value) return;
  showDropdown.value = false;
  triggerRef.value?.focus();
};

const toggleDropdown = async () => {
  if (showDropdown.value) {
    closeDropdown();
    return;
  }
  showDropdown.value = true;
  await nextTick();
  updatePosition();
};

// The teleported menu is out of the scrolling panel's flow, so it has to be
// re-anchored while any ancestor scrolls.
useEventListener(
  window,
  'scroll',
  () => showDropdown.value && updatePosition(),
  { capture: true, passive: true }
);

const handleClickOutside = event => {
  if (triggerRef.value?.contains(event.target)) return;
  // No focus restore here: the pointer that dismissed the menu decides where
  // focus lands, the same as any other click on the page.
  showDropdown.value = false;
};

// Escape reaches the search input first, which clears it natively; the menu has
// to close on the same key rather than needing a second press.
useEventListener(document, 'keydown', event => {
  if (event.key !== 'Escape' || !showDropdown.value) return;
  closeDropdown();
});
</script>

<template>
  <button
    ref="triggerRef"
    class="flex items-center gap-1 px-2 py-1 rounded-md outline-dashed h-6 outline-1 outline-n-slate-6 hover:bg-n-alpha-2"
    :class="{ 'bg-n-alpha-2': showDropdown }"
    @click="toggleDropdown"
  >
    <span class="i-lucide-plus" />
    <span class="text-sm text-n-slate-11">
      {{ t('LABEL.TAG_BUTTON') }}
    </span>
  </button>
  <TeleportWithDirection>
    <DropdownMenu
      v-if="showDropdown"
      ref="dropdownRef"
      v-on-click-outside="handleClickOutside"
      data-popover-content
      :title="dropdownTitle"
      :menu-items="labelMenuItems"
      show-search
      :style="fixedPosition.style"
      class="w-48 !fixed !z-[100]"
      @action="emit('updateLabel', $event)"
    >
      <template #thumbnail="{ item }">
        <div
          class="rounded-sm size-2"
          :style="{ backgroundColor: item.thumbnail.color }"
        />
      </template>
    </DropdownMenu>
  </TeleportWithDirection>
</template>
